import { memo, useCallback } from 'react';

import { useTrackCanvasDeleted } from '@air/analytics';
import { ConfirmationModal } from '@air/component-confirmation-modal';

import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateCanvasesDeleteCanvasMutation } from '~/swr-hooks/canvases/useDeleteCanvas';

export type DeleteCanvasConfirmationModalProps = {
  boardId?: string;
  canvasIds: string[];
  onDelete?: () => void;
};

export const DeleteCanvasConfirmationModal = memo(
  ({ boardId, canvasIds, onDelete, onClose }: AirModalProps<DeleteCanvasConfirmationModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { mutateAsync: deleteCanvas, isPending } = usePrivateCanvasesDeleteCanvasMutation();
    const { selectedCanvasId, closeCanvas } = useSelectedCanvasId();
    const { trackCanvasDeleted } = useTrackCanvasDeleted();

    const handleConfirm = useCallback(async () => {
      if (canvasIds.length === 0 || !currentWorkspace?.id) {
        return;
      }

      try {
        await deleteCanvas({ workspaceId: currentWorkspace.id, canvasIds });

        for (const canvasId of canvasIds) {
          trackCanvasDeleted({
            canvas_id: canvasId,
            board_id: boardId,
            location: boardId ? 'board' : 'canvases',
          });
        }

        if (selectedCanvasId && canvasIds.includes(selectedCanvasId)) {
          closeCanvas();
        }

        onDelete?.();
        onClose();
      } catch {
        // useDeleteCanvas shows toast on error
      }
    }, [
      boardId,
      canvasIds,
      closeCanvas,
      currentWorkspace,
      deleteCanvas,
      onClose,
      onDelete,
      selectedCanvasId,
      trackCanvasDeleted,
    ]);

    const count = canvasIds.length;
    const title = count === 1 ? 'Delete this canvas?' : `Delete ${count} canvases?`;
    const description =
      count === 1
        ? 'This action cannot be undone. All content on this canvas will be permanently deleted.'
        : `This action cannot be undone. All content on these ${count} canvases will be permanently deleted.`;

    return (
      <ConfirmationModal
        id="delete-canvas-confirmation"
        isOpen
        onDismiss={onClose}
        title={title}
        description={description}
        ctaPrimaryText={isPending ? 'Deleting...' : 'Delete'}
        onConfirm={handleConfirm}
        isDangerous
        isLoading={isPending}
        isDisabled={isPending}
      />
    );
  },
);

DeleteCanvasConfirmationModal.displayName = 'DeleteCanvasConfirmationModal';
